import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { createCronJobAction, fetchCronJobs } from '../redux/slices/cronSlice';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import Spinner from '../components/Spinner';

const CronJobForm = () => {
  const location = useLocation();
  const cronJobFromClone = useMemo(() => location.state?.cronJob || {}, [location.state]);

  const [cronJob, setCronJob] = useState({
    name: '',
    command: '',
    description: '',
    status: 'active',
    scheduleType: 'simple',
    frequency: 'hourly',
    minutes: [],
    hours: [],
    days: Array.from({ length: 31 }, (_, i) => (i + 1).toString()),
    months: Array.from({ length: 12 }, (_, i) => (i + 1).toString()),
    weekdays: Array.from({ length: 7 }, (_, i) => i.toString()),
  });

  const [selectAll, setSelectAll] = useState({
    minutes: false,
    hours: false,
    days: true,
    months: true,
    weekdays: true,
  });

  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (cronJobFromClone && Object.keys(cronJobFromClone).length > 0) {
      setCronJob((prev) => ({
        ...prev,
        ...cronJobFromClone,
      }));
    }
  }, [cronJobFromClone]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    dispatch(createCronJobAction(cronJob))
      .then(() => {
        toast.success('Cron Job successfully created!');
        dispatch(fetchCronJobs());
        navigate('/');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleScheduleChange = (field, value) => {
    setCronJob((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleMultiSelect = (field, options) => {
    setCronJob((prev) => ({
      ...prev,
      [field]: Array.from(options, (opt) => opt.value),
    }));
  };

  const handleSelectAllToggle = (field, allValues) => {
    setSelectAll((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
    setCronJob((prev) => ({
      ...prev,
      [field]: selectAll[field] ? [] : allValues.map((v) => v.toString()),
    }));
  };

  const createMultiSelectSection = (label, field, options, getOptionLabel) => (
    <div className="mb-4">
      <label className="font-bold">{label}:</label>
      <div className="flex items-center space-x-4 mb-2">
        <div>
          <input
            type="radio"
            name={`${field}Mode`}
            checked={selectAll[field]}
            onChange={() => handleSelectAllToggle(field, options)}
            className="mr-2"
          />
          <label className="text-sm">All</label>
        </div>
        <div>
          <input
            type="radio"
            name={`${field}Mode`}
            checked={!selectAll[field]}
            onChange={() => handleSelectAllToggle(field, [])}
            className="mr-2"
          />
          <label className="text-sm">Selected</label>
        </div>
      </div>
      <select
        multiple
        disabled={selectAll[field]}
        value={cronJob[field]}
        onChange={(e) => handleMultiSelect(field, e.target.selectedOptions)}
        className="w-full p-2 border border-gray-300 rounded-lg"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {getOptionLabel(option)}
          </option>
        ))}
      </select>
    </div>
  );

  const getMonthName = (monthIndex) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    return months[monthIndex - 1];
  };

  const getDayName = (dayIndex) => {
    const days = [
      'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
    ];
    return days[dayIndex];
  };

  return (
    <div className="container mx-auto px-6 py-4 relative overflow-x-hidden">
      {loading && <Spinner />}
      <Navbar
        title="Cron Job Scheduler"
        subtitle="Schedule And Automate Tasks With Ease."
        buttonLabel="Back To Dashboard"
        buttonAction={() => navigate('/')}
      />

      <h2 className="text-xl font-semibold mt-28">
        Streamline Your Workflow – Schedule Tasks with Ease!
      </h2>

      <form onSubmit={handleSubmit}>
        <div className="mb-6 mt-6">
          <div className="bg-[#4B76A7] text-white px-4 py-2 mb-4">Execution Details</div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-4">
            <div>
              <label className="block mb-2">Name*</label>
              <input
                type="text"
                placeholder="Enter name"
                value={cronJob.name}
                onChange={(e) => handleScheduleChange('name', e.target.value)}
                required
                className="w-full p-2 border rounded-lg shadow-sm"
              />
            </div>
            <div>
              <label className="block mb-2">Command*</label>
              <input
                type="text"
                placeholder="Enter Command"
                value={cronJob.command}
                onChange={(e) => handleScheduleChange('command', e.target.value)}
                required
                className="w-full p-2 border rounded-lg shadow-sm"
              />
            </div>
            <div>
              <label className="block mb-2">Description*</label>
              <input
                type="text"
                placeholder="Enter Description"
                value={cronJob.description}
                onChange={(e) => handleScheduleChange('description', e.target.value)}
                className="w-full p-2 border rounded-lg shadow-sm"
              />
            </div>
          </div>

          <div className="inline-flex rounded-md px-4 py-2 overflow-hidden border">
            <button
              type="button"
              onClick={() => handleScheduleChange('status', 'active')}
              className={`px-4 py-1 rounded-lg ${cronJob.status === 'active' ? 'bg-[#8B74BD] text-white' : 'bg-white'}`}
            >
              Active
            </button>
            <button
              type="button"
              onClick={() => handleScheduleChange('status', 'inactive')}
              className={`px-4 py-1 rounded-lg ${cronJob.status === 'inactive' ? 'bg-[#8B74BD] text-white' : 'bg-white'}`}
            >
              Inactive
            </button>
          </div>
        </div>

        <div className="mb-6">
          <div className="bg-[#4B76A7] text-white px-4 py-2 flex items-center">
            <span className="mr-4">Schedule Timing</span>
            <div className="inline-flex rounded-md px-4 py-2 overflow-hidden bg-white">
              <button
                type="button"
                onClick={() => handleScheduleChange('scheduleType', 'simple')}
                className={`px-4 py-1 rounded-lg ${cronJob.scheduleType === 'simple' ? 'bg-[#8B74BD] text-white' : 'text-gray-700'}`}
              >
                Simple
              </button>
              <button
                type="button"
                onClick={() => handleScheduleChange('scheduleType', 'custom')}
                className={`px-4 py-1 rounded-lg ${cronJob.scheduleType === 'custom' ? 'bg-[#8B74BD] text-white' : 'text-gray-700'}`}
              >
                Custom
              </button>
            </div>
          </div>

          {cronJob.scheduleType === 'simple' ? (
            <div className="mt-4">
              <label className="block mb-2">Frequency</label>
              <select
                value={cronJob.frequency}
                onChange={(e) => handleScheduleChange('frequency', e.target.value)}
                className="w-48 p-2 border rounded-lg shadow-sm"
              >
                <option value="custom">Custom</option>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily (AT Midnight)</option>
                <option value="weekly">Weekly (On Sunday)</option>
                <option value="monthly">Monthly (On The 1st)</option>
              </select>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-4">
              <div>
                <label className="block mb-2">Minutes</label>
                <div className="border rounded-lg p-2 max-h-[300px] overflow-y-auto">
                  {createMultiSelectSection(
                    'Minutes',
                    'minutes',
                    Array.from({ length: 60 }, (_, i) => i),
                    (option) => option
                  )}
                </div>
              </div>
              <div>
                <label className="block mb-2">Hours</label>
                <div className="border rounded-lg p-2 max-h-[300px] overflow-y-auto">
                  {createMultiSelectSection(
                    'Hours',
                    'hours',
                    Array.from({ length: 24 }, (_, i) => i),
                    (option) => option
                  )}
                </div>
              </div>

              <div>
                <label className="block mb-2">Days</label>
                <div className="border rounded-lg p-2 max-h-[300px] overflow-y-auto">
                  {createMultiSelectSection(
                    'Days',
                    'days',
                    Array.from({ length: 31 }, (_, i) => i + 1),
                    (option) => option
                  )}
                </div>
              </div>
              <div>
                <label className="block mb-2">Months</label>
                <div className="border rounded-lg p-2 max-h-[300px] overflow-y-auto">
                  {createMultiSelectSection(
                    'Months',
                    'months',
                    Array.from({ length: 12 }, (_, i) => i + 1),
                    getMonthName
                  )}
                </div>
              </div>
              <div>
                <label className="block mb-2">Weekdays</label>
                <div className="border rounded-lg p-2 max-h-[300px] overflow-y-auto">
                  {createMultiSelectSection(
                    'Weekdays',
                    'weekdays',
                    Array.from({ length: 7 }, (_, i) => i),
                    getDayName
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end mt-8">
          <button
            type="submit"
            className="bg-gradient-to-r from-steelblue to-darkblue text-white px-6 py-2 rounded-lg shadow-md hover:bg-[#3d6087]"
          >
            Create Cron Job
          </button>
        </div>
      </form>
    </div>
  );
};

export default CronJobForm;
